import { isActionOf } from 'typesafe-actions'
import { from, of } from 'rxjs'
import { catchError, exhaustMap, filter, map, mergeMap } from 'rxjs/operators'
import { toast } from 'react-toastify'
import { lowerCase } from 'lodash'
import { IntroducerStatusEnum } from '@sparling/financial-common'
import { AnyAction } from 'redux'
import { RootEpic } from '../types'
import {
  getIntroducerUsersRequest,
  getIntroducerUsersSuccess,
  getIntroducerUsersFailure,
  introducerDataUpdateRequest,
  introducerDataUpdateSuccess,
  introducerDataUpdateFailure,
  fetchUsersForReassignRequest,
  fetchUsersForReassignSuccess,
  fetchUsersForReassignFailure,
  assignIntroducerUserRequest,
  assignIntroducerUserSuccess,
  assignIntroducerUserFailure,
  reassignIntroducerUserRequest,
  reassignIntroducerUserSuccess,
  reassignIntroducerUserFailure,
  deleteIntroducerUserRequest,
  deleteIntroducerUserSuccess,
  deleteIntroducerUserFailure,
  getAssignableReferralsRequest,
  getAssignableReferralsSuccess,
  getAssignableReferralsFailure,
} from '../actions'

export const fetchIntroducerUsersEpic: RootEpic = (action$, state$, { fetchUserReferrals }) =>
  action$.pipe(
    filter(isActionOf([getIntroducerUsersRequest])),
    exhaustMap(({ payload }) =>
      from(
        fetchUserReferrals({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => getIntroducerUsersSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch introducer users.')
          return of(getIntroducerUsersFailure(message))
        }),
      ),
    ),
  )

export const introducerDataUpdateEpic: RootEpic = (action$, state$, { introducerDataUpdate }) =>
  action$.pipe(
    filter(isActionOf([introducerDataUpdateRequest])),
    exhaustMap(({ payload: { idSf, ...rest } }) =>
      from(introducerDataUpdate({ token: state$.value.app.authToken as string, idSf, ...rest })).pipe(
        map((response) => {
          if (response.success)
            toast.success(
              `Request successfully ${lowerCase(
                rest.introducerStatus === IntroducerStatusEnum.REJECTED ? 'DECLINED' : rest.introducerStatus,
              )}.`,
            )
          return introducerDataUpdateSuccess(response)
        }),
        catchError((message) => {
          toast.error(message)
          return of(introducerDataUpdateFailure(message))
        }),
      ),
    ),
  )

export const fetchUsersForReassignEpic: RootEpic = (action$, state$, { fetchUsersForReassign }) =>
  action$.pipe(
    filter(isActionOf([fetchUsersForReassignRequest])),
    exhaustMap(() =>
      from(fetchUsersForReassign({ token: state$.value.app.authToken as string })).pipe(
        map((response) => fetchUsersForReassignSuccess(response)),
        catchError((message) => {
          toast.error(message)
          return of(fetchUsersForReassignFailure(message))
        }),
      ),
    ),
  )

export const fetchAssignableReferralsEpic: RootEpic = (action$, state$, { fetchAssignableReferrals }) =>
  action$.pipe(
    filter(isActionOf([getAssignableReferralsRequest])),
    exhaustMap(() =>
      from(fetchAssignableReferrals({ token: state$.value.app.authToken as string })).pipe(
        map((response) => getAssignableReferralsSuccess(response.data)),
        catchError((message) => {
          toast.error(message)
          return of(getAssignableReferralsFailure(message))
        }),
      ),
    ),
  )

export const assignIntroducerUserEpic: RootEpic = (action$, state$, { assignIntroducerUser }) =>
  action$.pipe(
    filter(isActionOf([assignIntroducerUserRequest])),
    exhaustMap(({ payload }) =>
      from(assignIntroducerUser({ token: state$.value.app.authToken as string, ...payload })).pipe(
        map((response) => {
          toast.success('User assigned successfully.')
          return assignIntroducerUserSuccess(response)
        }),
        catchError((message) => {
          toast.error(message)
          return of(assignIntroducerUserFailure(message))
        }),
      ),
    ),
  )

export const reassignIntroducerUserEpic: RootEpic = (action$, state$, { reassignIntroducerUser }) =>
  action$.pipe(
    filter(isActionOf([reassignIntroducerUserRequest])),
    exhaustMap(({ payload }) =>
      from(reassignIntroducerUser({ token: state$.value.app.authToken as string, ...payload })).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = [reassignIntroducerUserSuccess(response)]
          if (response.success) {
            toast.success('User reassigned successfully.')
            actions.push(getIntroducerUsersRequest({ introducerId: payload.introducerId, page: 1, count: 10 }))
          }
          return of(...actions)
        }),
        catchError((message) => {
          toast.error(message)
          return of(reassignIntroducerUserFailure(message))
        }),
      ),
    ),
  )

export const deleteIntroducerUsersEpic: RootEpic = (action$, state$, { deleteUserReferrals }) =>
  action$.pipe(
    filter(isActionOf([deleteIntroducerUserRequest])),
    exhaustMap(({ payload }) =>
      from(
        deleteUserReferrals({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const { userId } = state$.value.introducer
          const actions: Array<AnyAction> = [deleteIntroducerUserSuccess(response)]
          if (response.success && userId) {
            toast.success('Introduced client removed successfully.')
            actions.push(getIntroducerUsersRequest({ introducerId: userId, page: 1, count: 10 }))
          }
          return of(...actions)
        }),
        catchError((message) => {
          toast.error('Failed to remove introducer client.')
          return of(deleteIntroducerUserFailure(message))
        }),
      ),
    ),
  )

export default [
  fetchIntroducerUsersEpic,
  introducerDataUpdateEpic,
  fetchUsersForReassignEpic,
  fetchAssignableReferralsEpic,
  assignIntroducerUserEpic,
  reassignIntroducerUserEpic,
  deleteIntroducerUsersEpic,
]
